import { Inputs } from "@thisgorilla/primate-ds";
import { Field, FieldProps } from "formik";
import { Fragment, useContext, useState } from "react";
import styled from "styled-components";

import { CreatePostModalContext } from "..";
import instance from "../../../../../../../axios/axios-user";
import { hideScrollBar } from "../../../../../../../commonStyles";
import { useAppSelector } from "../../../../../../../redux";
import {
  CREATE_POST_RICH_TEXT_AREA_HEIGHT,
  getMentionList,
  RICHTEXT_INITIAL_VALUE,
} from "../../../../../../../utils";
import { ModalContext } from "../../../../../../Common/Modal";
import { FlexColumn } from "../../../../../../Common/Styles";
import { CreatePostModalProps } from "../CreatePostModal";
import ModalFooter from "../ModalFooter";
import Audio from "./Audio";
import CreatePoll from "./CreatePoll";
import CreateSlider from "./CreateSlider";
import GIF from "./GIF";
import ImageUpload from "./ImageUpload";
import Topics from "./Topics";
import UrlPreview from "./UrlPreview";
import { findCustomURL } from "./utils";
import Video from "./Video";

const ModalContent = () => {
  const [isPreviewShow, setIsPreviewShow] = useState(true);
  const { post_theme, suggestedUsers } = useAppSelector((state) => state.me);
  const { footerRef } = useContext(ModalContext);
  const {
    pollOpen,
    setUrlData,
    urlData,
    sliderOpen,
    isTopicOpen,
    setPostContentOrder,
    postContentOrder,
    isAudioOpen,
    isVideoOpen,
  } = useContext(CreatePostModalContext);
  const fieldNameContent = "content";

  const renderPostContent: Record<
    CreatePostModalProps.PostContentTypes,
    React.ReactNode
  > = {
    url: urlData && isPreviewShow && (
      <UrlPreview setIsPreviewShow={setIsPreviewShow} />
    ),
    poll: pollOpen && <CreatePoll />,
    slider: sliderOpen && <CreateSlider />,
    topic: isTopicOpen && <Topics />,
    image: <ImageUpload />,
    audio: isAudioOpen && <Audio />,
    video: isVideoOpen && <Video />,
    gif: <GIF />,
  };

  return (
    <ModalContentContainer
      id="create-post-modal-content"
      $footerHeight={footerRef.current?.clientHeight || 0}
    >
      <Field key={fieldNameContent} name={fieldNameContent}>
        {({ field, form }: FieldProps) => {
          const { value } = field;
          const { setFieldValue, validateField } = form;

          return (
            <Inputs.RichTextarea
              axiosInstance={instance}
              fieldName={fieldNameContent}
              idPrefix="richtext"
              placeholder={post_theme}
              minHeight={CREATE_POST_RICH_TEXT_AREA_HEIGHT}
              variant="Post"
              isRequired
              mentionList={getMentionList(suggestedUsers)}
              onBlur={() => {
                validateField(fieldNameContent);
              }}
              value={value}
              onChangeHandler={(state, html) => {
                const urlinfo = findCustomURL(state.toJSON().root);
                setUrlData(urlinfo);
                setPostContentOrder((prev) =>
                  urlinfo
                    ? prev.includes("url")
                      ? prev
                      : [...prev, "url"]
                    : prev.filter((type) => type !== "url")
                );

                if (!html || html === RICHTEXT_INITIAL_VALUE) {
                  setFieldValue(fieldNameContent, "");
                } else {
                  setFieldValue(fieldNameContent, html);
                  setTimeout(() => {
                    validateField(fieldNameContent);
                  }, 0);
                }
              }}
              scrollAreaId="create-post-modal-content"
              footerComponent={
                <>
                  {postContentOrder.length > 0 && (
                    <FlexColumn $gapInPx={16}>
                      {postContentOrder.map((type, index) => (
                        <Fragment key={index}>
                          {renderPostContent[type]}
                        </Fragment>
                      ))}
                    </FlexColumn>
                  )}
                  <ModalFooter />
                </>
              }
            />
          );
        }}
      </Field>
    </ModalContentContainer>
  );
};

export default ModalContent;

const ModalContentContainer = styled.div<{ $footerHeight: number }>`
  max-height: calc(100vh - 277px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-4"].value}rem
    ${({ $footerHeight }) => $footerHeight + 8}px;

  ${hideScrollBar}
`;
